import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, TrendingUp, AlertTriangle, Trophy, Wallet, Activity, Clock, Target } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { MessageThread } from "@/components/MessageThread";
import { formatEuro } from "@/components/AutoTextarea";

const fmtDate = (iso) => {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return "–"; }
};

export default function RepAnalytics() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/analytics/user/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-50">
        <AppHeader title="Auswertung" back="/manager/team" />
        <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#00E575]" size={32} /></div>
      </div>
    );
  }
  if (!data) return null;
  const t = data.totals;
  const maxFunnel = Math.max(1, ...data.funnel.map((f) => f.reached));

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      <AppHeader title={`${data.user.first_name} ${data.user.last_name}`} back="/manager/team" />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div>
          <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575] mb-1">Vertrieb-Auswertung</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">{data.user.first_name} {data.user.last_name}</h1>
          <p className="text-zinc-400 mt-1 text-sm">{data.user.email}</p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="rep-kpis">
          <KpiCard label="Projekte" value={t.projects} icon={Activity} accent="text-zinc-100" />
          <KpiCard label="Angebote" value={t.offers} icon={TrendingUp} accent="text-zinc-100" />
          <KpiCard label="Unterschrieben" value={t.offers_signed} icon={Trophy} accent="text-[#00E575]" sub={`${t.sign_rate_pct}% Quote`} />
          <KpiCard label="Signed €" value={`${formatEuro(t.signed_volume_net)} €`} icon={Wallet} accent="text-[#00E575]" sub="netto" />
          <KpiCard label="Ø Angebot" value={`${formatEuro(t.avg_offer_value_net)} €`} icon={Target} accent="text-zinc-100" sub="netto" />
        </div>

        {/* Schwachstellen-Hinweise */}
        {data.weak_spot_hints?.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5" data-testid="weak-spots">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
              <h3 className="font-display font-bold text-[15px] text-amber-200">Mögliche Schwachstellen</h3>
            </div>
            <ul className="space-y-1.5 ml-7 text-[13px] text-amber-100/90 list-disc list-outside">
              {data.weak_spot_hints.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </div>
        )}

        {/* Conversion Funnel */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
          <h3 className="font-display text-lg font-semibold mb-3">Conversion-Funnel</h3>
          <p className="text-[12px] text-zinc-400 mb-4 font-medium">Wie viele Projekte haben jede Stufe erreicht (kumulativ).</p>
          <div className="space-y-2" data-testid="funnel">
            {data.funnel.map((f) => (
              <div key={f.key} className="flex items-center gap-3">
                <div className="w-44 sm:w-52 text-[13px] truncate text-zinc-200">{f.label}</div>
                <div className="flex-1 h-7 bg-zinc-900 rounded-lg overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-[#00E575] to-[#00C964] rounded-lg transition-all"
                    style={{ width: `${(f.reached / maxFunnel) * 100}%`, minWidth: f.reached > 0 ? "8%" : "0" }}
                  />
                  <div className="absolute inset-0 flex items-center justify-end pr-3 text-[12px] font-mono font-bold text-white drop-shadow">
                    {f.reached} <span className="text-zinc-300 ml-1">({f.rate_pct}%)</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Durchlaufzeit pro Stufe */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
          <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Clock size={18} className="text-[#00E575]" /> Durchschnittliche Zeit pro Stufe</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="avg-days">
            {Object.entries(data.avg_days_per_stage).filter(([k]) => k !== "lead").map(([k, v]) => {
              const label = data.funnel.find((f) => f.key === k)?.label || k;
              return (
                <div key={k} className="bg-[#09090B]/60 rounded-lg px-3 py-2.5">
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold truncate">{label}</div>
                  <div className={`font-display font-bold text-[16px] mt-0.5 ${v > 21 ? "text-amber-400" : "text-zinc-100"}`}>
                    {v !== null ? `${v} T` : "–"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stale projects */}
        {data.stale_projects?.length > 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="stale-list">
            <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-400" /> Liegengebliebene Projekte
              <span className="text-[12px] text-zinc-300 font-normal">({data.stale_projects.length})</span>
            </h3>
            <p className="text-[12px] text-zinc-400 mb-3 font-medium">Projekte ohne Aktivität seit mehr als 14 Tagen.</p>
            <div className="space-y-1.5">
              {data.stale_projects.slice(0, 8).map((s) => (
                <Link
                  key={s.id}
                  to={`/projects/${s.id}`}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 bg-[#09090B] hover:bg-zinc-900 rounded-lg border border-zinc-800/60 transition"
                  data-testid={`stale-${s.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-mono text-[#00E575] truncate">{s.project_number}</div>
                    <div className="text-[12px] text-zinc-300 truncate">Status: {s.stage}</div>
                  </div>
                  <div className="text-[12px] font-bold text-amber-400 font-mono flex-shrink-0">{s.age_days} T</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Recent activity */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
          <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Activity size={18} className="text-[#00E575]" /> Letzte Aktivitäten</h3>
          {data.recent_activity.length === 0 ? (
            <div className="text-[13px] text-zinc-500 text-center py-6">Noch keine Aktivitäten erfasst.</div>
          ) : (
            <div className="space-y-1.5">
              {data.recent_activity.slice(0, 15).map((h) => (
                <div key={h.id} className="flex items-start gap-3 px-3 py-2 bg-[#09090B]/60 rounded-lg" data-testid={`activity-${h.id}`}>
                  <div className="text-[12px] font-mono text-zinc-300 w-24 flex-shrink-0">{fmtDate(h.created_at)}</div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-zinc-200">{h.action_type}</div>
                    <div className="text-[12px] text-zinc-400 truncate font-medium">{h.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Direct messages with this rep */}
        <div className="mt-8">
          <h2 className="font-display text-lg font-semibold mb-3">Nachrichten</h2>
          <MessageThread scopeType="user" scopeId={userId} />
        </div>
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
        <Icon size={16} className="text-zinc-500" />
      </div>
      <div className={`font-display font-bold text-2xl ${accent}`}>{value}</div>
      {sub && <div className="text-[12px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}
